import type { ToolDelivery, WireMode } from '@edge/edge-ai'
import type { ComponentType } from '@/types/schemas/workspace'

// ─── Event Names ───

export const EVENTS = {
  TICKER_SELECTED: 'ticker_selected',
  PANEL_ADDED: 'panel_added',
  PANEL_REMOVED: 'panel_removed',
  UPGRADE_PROMPT_SHOWN: 'upgrade_prompt_shown',
  LAYOUT_CREATED: 'layout_created',
  LAYOUT_SWITCHED: 'layout_switched',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  RESEARCH_TAB_VIEWED: 'research_tab_viewed',
  // A lazy chunk 404'd, almost always a stale tab after a deploy. Reported
  // as a product event (not an exception) so a bad rollout shows up as a
  // rate without spending the bounded exception budget.
  CHUNK_LOAD_FAILED: 'chunk_load_failed',
  // Workspace persistence: recovered conditions the user usually survives,
  // which is exactly why nothing else surfaces them. Product events, not
  // exceptions, so they stay queryable as funnels and cohorts. See
  // src/workspace/README.md "Resilience Patterns" for what each guards.
  WORKSPACE_EMPTY_OVERWRITE_REFUSED: 'workspace_empty_overwrite_refused',
  WORKSPACE_SAVE_QUEUE_OVERFLOW: 'workspace_save_queue_overflow',
  WORKSPACE_MUTATION_QUEUE_OVERFLOW: 'workspace_mutation_queue_overflow',
  WORKSPACE_MARKED_STALE: 'workspace_marked_stale',
  WORKSPACE_REVISIONED_SAVE_DEFERRED: 'workspace_revisioned_save_deferred',
  WORKSPACE_DOC_VALIDATION_FAILED: 'workspace_doc_validation_failed',
  WORKSPACE_CONNECTION_CHANGED: 'workspace_connection_changed',
  // Onboarding funnel, emitted from the onboarding store (the only place
  // that sees every path). Stamped with config_version and persona so a
  // rewritten interview does not reset the funnel.
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_QUESTION_ANSWERED: 'onboarding_question_answered',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',
  ONBOARDING_PROVISIONED: 'onboarding_provisioned',
  ONBOARDING_PROVISION_FAILED: 'onboarding_provision_failed',
  CHECKLIST_ITEM_COMPLETED: 'checklist_item_completed',
  CHECKLIST_COMPLETED: 'checklist_completed',
  CHECKLIST_DISMISSED: 'checklist_dismissed',
  CHECKLIST_MINIMIZED: 'checklist_minimized',
  CHECKLIST_REOPENED: 'checklist_reopened',
  // "Fully onboarded", defined by behavior (2 of 3 first-time actions), not
  // by the checklist. Fires once per user. The 7-day window is a PostHog
  // cohort rule, not client code, so it can be retuned without a deploy.
  USER_ACTIVATED: 'user_activated',
  // The user saw a failure toast. Handled-but-broken cases that exception
  // tracking never sees. Deduped and capped per session in the store.
  ERROR_SHOWN: 'error_shown',
  // Saved-drawing restore could not rely on the chart's one-shot ready signal.
  // Drawings are safe either way; this measures how often and where. Capped
  // per session in features/charts/restore-diagnostics.ts.
  CHART_RESTORE_DIAGNOSTIC: 'chart_restore_diagnostic',
  // Expression-library lifecycle under G4 (sticky-snapshot). Fired
  // from `useExpressionLibrary` after a successful save.
  EXPRESSION_TEMPLATE_EDITED: 'expression_template_edited',
  EXPRESSION_TEMPLATE_DELETED: 'expression_template_deleted',
  EXPRESSION_TEMPLATE_RESTORED: 'expression_template_restored',
  // A user asked for a missing how-to guide from the AI's "Request this"
  // button (the FAQ / knowledge-base backlog).
  COMPANION_CONTENT_REQUESTED: 'companion_content_requested',
  // Signup to paid conversion funnel. Pageviews cover arriving on each route;
  // these mark the actions and failures a pageview cannot see. The paid step
  // itself is `subscription_created`, captured server-side by the Stripe
  // webhook (edge-user-api telemetry.py) because checkout happens off-domain.
  SIGNUP_SUBMITTED: 'signup_submitted',
  SIGNUP_COMPLETED: 'signup_completed',
  SIGNUP_FAILED: 'signup_failed',
  LOGIN_FAILED: 'login_failed',
  EMAIL_VERIFIED: 'email_verified',
  EMAIL_VERIFICATION_FAILED: 'email_verification_failed',
  VERIFICATION_CODE_RESENT: 'verification_code_resent',
  // /billing renders two steps under one URL, so the step is an event property.
  BILLING_STEP_VIEWED: 'billing_step_viewed',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_FAILED: 'checkout_failed',
  CHECKOUT_RETURNED: 'checkout_returned',
  CHECKOUT_ACTIVATION_TIMED_OUT: 'checkout_activation_timed_out',
  // Failures whose toast text loses the detail (error_shown turns numbers into
  // N), so the status and timing ride on their own events.
  EXPORT_FAILED: 'export_failed',
  EXPORT_COMPLETED: 'export_completed',
  HISTORY_RECORDS_FAILED: 'history_records_failed',
  // One per companion turn (TEL, 2026-10-19): which path answered, time to
  // first word, rounds and tools. Joins the server's companion_turn_routed,
  // $ai_generation and analyst_turn events by client_turn_id.
  AI_TURN: 'ai_turn',
} as const

// ─── Event Payloads ───

export interface TickerSelectedPayload {
  symbol: string
  source: 'scanner' | 'watchlist' | 'search' | 'history' | 'other'
}

export interface PanelAddedPayload {
  component_type: ComponentType
}

export interface PanelRemovedPayload {
  component_type: ComponentType
}

export interface UpgradePromptShownPayload {
  feature: string
  component_type?: ComponentType
  /** Whether the plan lacked the feature or had spent its allowance. */
  reason?: 'locked' | 'limit'
  /** The tier the pitch offered: the cheapest one that solves the refusal. */
  target_tier?: string
}

export interface LayoutCreatedPayload {
  layout_id: string
  workspace_id: string
}

export interface LayoutSwitchedPayload {
  layout_id: string
  workspace_id: string
}

export interface ResearchTabViewedPayload {
  tab: string
  ticker: string
}

export interface ChunkLoadFailedPayload {
  boundary: 'page' | 'panel'
}

// Workspace payloads carry ids and counts only: never panel contents, user
// text, or ticker lists.

export interface WorkspaceEmptyOverwriteRefusedPayload {
  workspace_id: string
  layout_id: string
  local_panel_count: number
  local_has_mosaic_tree: boolean
  local_grid_cells: number
}

export interface WorkspaceQueueOverflowPayload {
  workspace_id: string
  /** Absent on the controller (tree) queue, which is not per-layout. */
  layout_id?: string
  queue_size: number
}

export interface WorkspaceMarkedStalePayload {
  workspace_id: string
  layout_id: string
  /** The shape of failure, not its text: 'conflict' | 'overflow' | 'generic'. */
  reason: 'conflict' | 'overflow' | 'generic'
}

export interface WorkspaceRevisionedSaveDeferredPayload {
  workspace_id: string
  layout_id: string
}

export interface WorkspaceDocValidationFailedPayload {
  workspace_id: string
  /** Absent for the meta document. */
  layout_id?: string
  kind: 'doc' | 'meta'
  issue_count: number
}

export interface WorkspaceConnectionChangedPayload {
  workspace_id: string
  from: 'connecting' | 'connected' | 'disconnected'
  to: 'connecting' | 'connected' | 'disconnected'
}

export interface OnboardingStartedPayload {
  config_version: number
}

export interface OnboardingQuestionAnsweredPayload {
  question_id: string
  option_id: string
  /** Position in the interview, so drop-off reads by order even when skipIf elides a question. */
  index: number
  config_version: number | null
}

export interface OnboardingCompletedPayload {
  persona: string
  config_version: number | null
  duration_ms: number
}

export interface OnboardingSkippedPayload {
  config_version: number | null
  questions_answered: number
}

export interface OnboardingProvisionedPayload {
  persona: string
  preset_id: string
  landing_route: string | null
}

export interface OnboardingProvisionFailedPayload {
  step: 'workspace' | 'watchlist' | 'track'
  persona: string
  /** The error's message, capped. No user content lives in these errors. */
  message: string
}

export interface ChecklistItemCompletedPayload {
  checklist_id: string
  item_id: string
  done_count: number
  total: number
  persona: string | null
}

export interface ChecklistCompletedPayload {
  checklist_id: string
  persona: string | null
  duration_ms: number
}

export interface ChecklistDismissedPayload {
  checklist_id: string
  done_count: number
  total: number
  /** Whether every item was done at dismissal: a finish, not a give-up. */
  complete: boolean
}

/** The card's X folds it into a corner pill; only "Hide checklist" dismisses. */
export interface ChecklistMinimizedPayload {
  checklist_id: string
  done_count: number
  total: number
}

export interface ChecklistReopenedPayload {
  checklist_id: string
  done_count: number
  total: number
}

export interface ChartRestoreDiagnosticPayload {
  /** `ready_recovered`: a settings reload superseded the first load.
   *  `ready_missed`: bars restored the drawings and ready never followed. */
  kind: 'ready_recovered' | 'ready_missed'
  /** The chart's timeframe label, e.g. '5m', '1D'. */
  period: string
  /** True for a cell of a multi-chart grid (the only place it was seen). */
  in_grid: boolean
}

export interface ErrorShownPayload {
  /** The caller's stable notification key, when it set one. */
  source: string | null
  /** Toast text with tickers and numbers normalized; never the raw string. */
  message_key: string
  /** Ticker symbols the toast names (max 20), so failing symbols stay
   *  visible even though message_key collapses them to TICKER. */
  symbols?: string[]
}

export interface UserActivatedPayload {
  /** How the user came through the door. A dimension of the funnel, never a stage. */
  entry_path: 'completed' | 'skipped' | 'abandoned' | 'never_started' | 'migrated'
  persona: string | null
  behaviors: string[]
  days_since_first_visit: number
}

export interface ExpressionTemplateEventPayload {
  namespace: 'realtime' | 'historical'
}

export interface CompanionContentRequestedPayload {
  /** The topic the guide search came back empty for. */
  topic: string
  /** The user's question as asked (preamble stripped), capped. */
  question: string
  page: string
  persona: 'learn' | 'analyst' | 'build'
}

// Funnel payloads never carry the email, name or raw error text. Failures
// report the auth provider's error NAME (e.g. UsernameExistsException), which
// is a diagnosis without user content.

export interface SignupFailedPayload {
  error_name: string
}

export interface LoginFailedPayload {
  error_name: string
  category:
    | 'credentials'
    | 'lockout'
    | 'disabled'
    | 'reset_required'
    | 'extra_step'
    | 'rate_limited'
    | 'network'
    | 'already_signed_in'
    | 'other'
  /** Failed sign-ins in a row in this tab; resets on a successful login. */
  attempt: number
  method: 'password'
}

export interface EmailVerifiedPayload {
  /** False when the auto sign-in after confirmation did not happen and the user was sent to /login. */
  auto_signed_in: boolean
}

export interface EmailVerificationFailedPayload {
  error_name: string
}

export interface BillingStepViewedPayload {
  step: 'compliance' | 'plans'
  cobra: boolean
  trial_eligible: boolean
}

export interface CheckoutStartedPayload {
  plan: string
  interval: 'monthly' | 'yearly'
  cobra: boolean
  trial_eligible: boolean
  is_professional: boolean
  /** A Rewardful affiliate referral cookie was present. */
  affiliate_referral: boolean
  affiliate_campaign: string | null
}

export interface CheckoutFailedPayload {
  plan: string
  status: number | null
}

export interface CheckoutActivationTimedOutPayload {
  polls: number
}

export interface ExportFailedPayload {
  reason: 'library_loading' | 'http' | 'network' | 'unknown'
  status: number | null
  /** The server's sentence for the refusal, capped at 120 characters; null when it sent none. */
  detail: string | null
  column_count: number
  offset: number
}

export interface ExportCompletedPayload {
  row_count: number
  column_count: number
  truncated: boolean
  duration_ms: number
}

/**
 * One per failed attempt, retry included. The request's shape only: nothing that
 * identifies a person and no filter values.
 */
export interface HistoryRecordsFailedPayload {
  /**
   * HTTP status (a CloudFront HTML error page's too), or null for a network
   * failure / timeout with no response.
   */
  status: number | null
  /** `http` for an HTTP error, else RTK's status (`FETCH_ERROR`, `PARSING_ERROR`, ...) or `unknown`. */
  error_kind: string
  /** The status behind a `PARSING_ERROR` (an HTML error page), else null. */
  original_status: number | null
  /** How long the request ran before failing; long ones point at a slow query. */
  duration_ms: number
  /** UTC minute of the hour the request started (0-59): MotherDuck stalls at the top of each hour. */
  started_minute: number
  /**
   * True when this failure was the one automatic retry after a gateway timeout.
   * A first attempt that gets retried reports false, so first-attempt 504s minus
   * retried failures counts the blocks the retry rescued.
   */
  retried: boolean
  /** Filter conditions sent (tags and the pattern leaf travel separately). */
  filter_count: number
  /** Days spanned by the `day0_date` range, or null when there is none. */
  date_span_days: number | null
  columns_count: number
  offset: number
  limit: number
  /** Sorted column ids, in order (the row-identity tiebreak excluded). */
  sort_columns: string[]
}

/**
 * One companion turn as the browser saw it (features/companion/ai-turn-telemetry.ts).
 * Ids, counts, times and tool names only: never what the trader or the AI wrote.
 */
export interface AiTurnPayload {
  /** The turn's id, sent on every request of the turn: the join key with the server's events. */
  client_turn_id: string
  path: 'chat' | 'analyst'
  modality: 'text' | 'voice'
  /**
   * Whether the turn could change the workspace: 'do' when it could, else 'ask'.
   * The Ask/Do toggle's names, kept so events from before and after the rename
   * compare (`toWireMode` in @edge/edge-ai maps from the turn's access).
   */
  mode: WireMode
  outcome: 'answered' | 'error' | 'stopped'
  /** Send to the first text delta; null when no text streamed. */
  ttfw_ms: number | null
  /** Send to the analyst's acknowledgement; null on the chat path. */
  ack_ms: number | null
  total_ms: number
  rounds: number
  /** Browser tools the model called, by name (get_tools counted apart). */
  tools_called: Record<string, number>
  tool_calls: number
  get_tools_lookups: number
  /** Tools the model called or asked to load that the round did not offer. */
  not_offered: string[]
  not_offered_calls: number
  prompt_tokens: number | null
  cached_tokens: number | null
  /**
   * How the turn's chat requests sent their tools (CACHE step 2, 2026-10-19):
   * the server's TOOL_DELIVERY, learned from the turn check.
   */
  tool_delivery: ToolDelivery
}

export type EventPayloadMap = {
  [EVENTS.TICKER_SELECTED]: TickerSelectedPayload
  [EVENTS.PANEL_ADDED]: PanelAddedPayload
  [EVENTS.PANEL_REMOVED]: PanelRemovedPayload
  [EVENTS.UPGRADE_PROMPT_SHOWN]: UpgradePromptShownPayload
  [EVENTS.LAYOUT_CREATED]: LayoutCreatedPayload
  [EVENTS.LAYOUT_SWITCHED]: LayoutSwitchedPayload
  [EVENTS.USER_LOGGED_IN]: Record<string, never>
  [EVENTS.USER_LOGGED_OUT]: Record<string, never>
  [EVENTS.RESEARCH_TAB_VIEWED]: ResearchTabViewedPayload
  [EVENTS.CHUNK_LOAD_FAILED]: ChunkLoadFailedPayload
  [EVENTS.WORKSPACE_EMPTY_OVERWRITE_REFUSED]: WorkspaceEmptyOverwriteRefusedPayload
  [EVENTS.WORKSPACE_SAVE_QUEUE_OVERFLOW]: WorkspaceQueueOverflowPayload
  [EVENTS.WORKSPACE_MUTATION_QUEUE_OVERFLOW]: WorkspaceQueueOverflowPayload
  [EVENTS.WORKSPACE_MARKED_STALE]: WorkspaceMarkedStalePayload
  [EVENTS.WORKSPACE_REVISIONED_SAVE_DEFERRED]: WorkspaceRevisionedSaveDeferredPayload
  [EVENTS.WORKSPACE_DOC_VALIDATION_FAILED]: WorkspaceDocValidationFailedPayload
  [EVENTS.WORKSPACE_CONNECTION_CHANGED]: WorkspaceConnectionChangedPayload
  [EVENTS.ONBOARDING_STARTED]: OnboardingStartedPayload
  [EVENTS.ONBOARDING_QUESTION_ANSWERED]: OnboardingQuestionAnsweredPayload
  [EVENTS.ONBOARDING_COMPLETED]: OnboardingCompletedPayload
  [EVENTS.ONBOARDING_SKIPPED]: OnboardingSkippedPayload
  [EVENTS.ONBOARDING_PROVISIONED]: OnboardingProvisionedPayload
  [EVENTS.ONBOARDING_PROVISION_FAILED]: OnboardingProvisionFailedPayload
  [EVENTS.CHECKLIST_ITEM_COMPLETED]: ChecklistItemCompletedPayload
  [EVENTS.CHECKLIST_COMPLETED]: ChecklistCompletedPayload
  [EVENTS.CHECKLIST_DISMISSED]: ChecklistDismissedPayload
  [EVENTS.CHECKLIST_MINIMIZED]: ChecklistMinimizedPayload
  [EVENTS.CHECKLIST_REOPENED]: ChecklistReopenedPayload
  [EVENTS.USER_ACTIVATED]: UserActivatedPayload
  [EVENTS.ERROR_SHOWN]: ErrorShownPayload
  [EVENTS.CHART_RESTORE_DIAGNOSTIC]: ChartRestoreDiagnosticPayload
  [EVENTS.EXPRESSION_TEMPLATE_EDITED]: ExpressionTemplateEventPayload
  [EVENTS.EXPRESSION_TEMPLATE_DELETED]: ExpressionTemplateEventPayload
  [EVENTS.EXPRESSION_TEMPLATE_RESTORED]: ExpressionTemplateEventPayload
  [EVENTS.COMPANION_CONTENT_REQUESTED]: CompanionContentRequestedPayload
  [EVENTS.SIGNUP_SUBMITTED]: Record<string, never>
  [EVENTS.SIGNUP_COMPLETED]: Record<string, never>
  [EVENTS.SIGNUP_FAILED]: SignupFailedPayload
  [EVENTS.LOGIN_FAILED]: LoginFailedPayload
  [EVENTS.EMAIL_VERIFIED]: EmailVerifiedPayload
  [EVENTS.EMAIL_VERIFICATION_FAILED]: EmailVerificationFailedPayload
  [EVENTS.VERIFICATION_CODE_RESENT]: Record<string, never>
  [EVENTS.BILLING_STEP_VIEWED]: BillingStepViewedPayload
  [EVENTS.CHECKOUT_STARTED]: CheckoutStartedPayload
  [EVENTS.CHECKOUT_FAILED]: CheckoutFailedPayload
  [EVENTS.CHECKOUT_RETURNED]: Record<string, never>
  [EVENTS.CHECKOUT_ACTIVATION_TIMED_OUT]: CheckoutActivationTimedOutPayload
  [EVENTS.EXPORT_FAILED]: ExportFailedPayload
  [EVENTS.EXPORT_COMPLETED]: ExportCompletedPayload
  [EVENTS.HISTORY_RECORDS_FAILED]: HistoryRecordsFailedPayload
  [EVENTS.AI_TURN]: AiTurnPayload
}
